import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { faHeartPulse } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";

const FoodIntoleranceTestingForm = () => {
    const navigate = useNavigate();
    const axios = useAxiosPrivate();
    const { createInfo, auth, userPolicy } = useAuth();

    const [formData, setFormData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        formData.name = auth?.user?.name
        formData.email = auth?.user?.email
        formData.dob = auth?.user?.date_of_birth
        formData.contact = auth?.user?.phone_number
        formData.schoolName = auth?.user?.client?.organisation?.name
        formData.schoolPostcode = auth?.user?.client?.organisation?.postcode
    }, [])

    const goBack = () => navigate(-1);

    const handleChange = (e, fieldName) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData((prev) => ({ ...prev, [fieldName]: value }));
    };

    const isFormValid = formData.name && formData.name.trim() !== '' &&
        formData.email && formData.email.trim() !== '' &&
        formData.dob && formData.dob.trim() !== '' &&
        formData.contact && formData.contact.trim() !== '' &&
        formData.schoolName && formData.schoolName.trim() !== '' &&
        formData.schoolPostcode && formData.schoolPostcode.trim() !== '' &&
        formData.bestTime && formData.bestTime.trim() !== '' &&
        formData.confirm;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const infoObj = {
            user: {
                name: formData.name || "",
                email: formData.email || "",
                phone_number: formData.contact || "",
                dob: formData.dob || "",
            },
            school: {
                schoolName: formData.schoolName || "",
                schoolPostcode: formData.schoolPostcode || "",
            },
            contact: formData.bestTime || "",
            extras: "",
        };

        const data = {
            info: JSON.stringify(infoObj),
            name: 'General Health',
            product: "food intolerance testing",
            type: 'staff',
            staff_id: auth?.user?.client?.staff_id,
            policy_id: userPolicy?.id,
            policy_reference: userPolicy?.reference,
            user_id: auth?.user?.id,
            client_id: auth?.user?.client_id
        }

        try {
            await axios.post('/tasks/create-from-enquiry', data).then((response) => {
                if (response.data.error) {
                    createInfo('error', "Enquiry Already Exists")
                } else {
                    createInfo('success', "Submitted Successfully.");
                }
            });
            setTimeout(() => {
                navigate("/referrals")
            }, 2000);
        } catch (err) {
            console.error(err);
            alert("There was an error submitting the request.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section>
            <div className="max-w-2xl mx-auto p-6 space-y-8 text-portal-dark-blue">
                <h1 className='text-[#41264B] text-2xl font-semibold border-b border-[#41264B]'>Food Intolerance Testing</h1>

                <section className="pb-6">
                    {!showForm ? (
                        <>
                            <div className="mt-2">
                                <h3 className="text-xl font-semibold text-[#41264B] mb-2">How it works</h3>
                                <p className="text-[#41264B] whitespace-pre-line">
                                    Food intolerance testing can help identify foods that may be contributing to symptoms. Once you’ve submitted your enquiry, we’ll contact you to discuss next steps and any eligibility or preparation details.
                                </p>
                            </div>

                            <div className="space-y-4 mt-4">
                                <div className="flex items-start gap-3 text-sm text-gray-300">
                                    <FontAwesomeIcon icon={faHeartPulse} className="text-[#407cb9] mt-1" />
                                    <p>
                                        <span className="text-[#407cb9] font-semibold">Discuss symptoms:</span>
                                        <span className='text-[#41264B]'> Tell us what you’re experiencing and what you’ve tried so far</span>
                                    </p>
                                </div>
                                <div className="flex items-start gap-3 text-sm text-gray-300">
                                    <FontAwesomeIcon icon={faHeartPulse} className="text-[#407cb9] mt-1" />
                                    <p>
                                        <span className="text-[#407cb9] font-semibold">Testing guidance:</span>
                                        <span className='text-[#41264B]'> We’ll explain the testing process and what information we need</span>
                                    </p>
                                </div>
                                <div className="flex items-start gap-3 text-sm text-gray-300">
                                    <FontAwesomeIcon icon={faHeartPulse} className="text-[#407cb9] mt-1" />
                                    <p>
                                        <span className="text-[#407cb9] font-semibold">Next steps:</span>
                                        <span className='text-[#41264B]'> We’ll contact you to arrange the most appropriate support</span>
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={() => setShowForm(true)}
                                className="mt-6 w-full bg-white text-[#41264B] py-3 rounded-lg font-semibold shadow-sm hover:shadow-lg transition-all p-2 border hover:border-[#41264B] hover:bg-[#BCA6C4]"
                            >
                                Request Help
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="mt-2 text-[#41264B] whitespace-pre-line">
                                Complete the form below to make your request for Food Intolerance Testing. All requests are confidential.
                            </p>

                            <form className="space-y-4 text-[#41264B] p-4 rounded-md mt-6" onSubmit={handleSubmit}>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Name <span className="text-red-600">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                                        placeholder="First & Last Name"
                                        value={formData.name || ""}
                                        onChange={(e) => handleChange(e, "name")}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Email Address <span className="text-red-600">*</span></label>
                                    <input
                                        type="email"
                                        className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                                        placeholder="Email Address"
                                        value={formData.email || ""}
                                        onChange={(e) => handleChange(e, "email")}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Date of Birth <span className="text-red-600">*</span></label>
                                    <input
                                        type="date"
                                        className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                                        placeholder="Select date of birth"
                                        value={formData.dob || ""}
                                        onChange={(e) => handleChange(e, "dob")}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Contact Number <span className="text-red-600">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                                        placeholder="Contact Number"
                                        value={formData.contact || ""}
                                        onChange={(e) => handleChange(e, "contact")}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">School Name <span className="text-red-600">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                                        placeholder="School Name"
                                        value={formData.schoolName || ""}
                                        onChange={(e) => handleChange(e, "schoolName")}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">School Postcode <span className="text-red-600">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                                        placeholder="School Postcode"
                                        value={formData.schoolPostcode || ""}
                                        onChange={(e) => handleChange(e, "schoolPostcode")}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">When is the best time to contact you? <span className="text-red-600">*</span></label>
                                    <select
                                        className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                                        value={formData.bestTime || ""}
                                        onChange={(e) => handleChange(e, "bestTime")}
                                    >
                                        <option disabled value="">Select an item</option>
                                        <option value="Morning">Morning</option>
                                        <option value="Afternoon">Afternoon</option>
                                        <option value="Evening">Evening</option>
                                    </select>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <input
                                        name="confirm"
                                        id="confirm"
                                        type="checkbox"
                                        checked={!!formData.confirm}
                                        onChange={(e) => handleChange(e, "confirm")}
                                    />
                                    <label htmlFor="confirm" className="text-sm">By Requesting a service, I am giving my permission for School Wellbeing and third-party service providers to contact me. For more information on how we collect and manage your data, please don't hesitate to contact School Wellbeing on 01773814404.</label>
                                </div>

                                {!isFormValid && Object.keys(formData).length > 0 && (
                                    <p className="text-sm text-red-600">Please fill in all required fields before submitting.</p>
                                )}

                                <div className="flex items-center justify-between space-x-4">
                                    <button
                                        type="submit"
                                        disabled={!isFormValid || submitting}
                                        className={`w-full py-2 rounded font-semibold mt-4 border-2 transition-all ${!isFormValid
                                            ? 'bg-gray-300 text-gray-400 cursor-not-allowed'
                                            : 'bg-white text-[#41264B] hover:shadow-lg hover:border-[#41264B] hover:bg-[#e6e3ea]'
                                            }`}
                                    >
                                        {submitting ? "Sending..." : "Send Request"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={goBack}
                                        className="w-full bg-gray-300 text-gray-800 py-2 rounded font-semibold mt-4 hover:bg-white border-2 border-gray-300"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </section>
            </div>
        </section>
    );
};

export default FoodIntoleranceTestingForm;
